import React, { useState } from 'react';
import { Box, Button, Skeleton, Typography } from '@mui/material';
import { saveAs } from 'file-saver';
import { FileDownloadOutlined, CloudUploadOutlined } from '@mui/icons-material';
import { DeleteModal } from 'components/Modals/DeleteModal';
import {
  ANNOTATIONS_COLOR_LEGEND_FIELD,
  ANNOTATIONS_OPACITY_FIELD,
  DISPLAYED_EXTERNAL_ANNOTATIONS,
} from 'components/ImageViewer/constants';
import {
  getUpdatedVisibility,
  handleDeleteAnnotationsAttachment,
  handleUploadAnnotationsAttachment,
} from 'components/ImageViewer/SlideViewerHeader/utils';
import { useDispatch, useSelector } from 'react-redux';
import {
  currentUserPreferencesSelector,
  updatePreferences,
} from 'store/slices/userDetailsSlice';
import { useSnackbar } from 'utilities/hooks/useSnackbar/useSnackbar';
import { useSlideViewerActionsStyles } from 'components/ImageViewer/SlideViewerHeader/styles';
import { ExportHaloAnnotationsConfirmationModal } from 'components/OrderSlidesList/components/ExportHaloAnnotationsConfirmationModal';
import { annotationsSelector } from 'store/slices/annotationsSlice';
import { UploadAnnotationsViewModal } from 'components/OrderSlidesList/ExtAnnotations/UploadAnnotationsModal';
import { LIGHT_GREY_2 } from 'utilities/colors';
import {
  useDeleteExternalSlideAnnotationsMutation,
  useLazyGetSlideQuery,
  useUploadExternalSlideAnnotationsMutation,
} from 'store/apis/slidesApi';
import {
  isExpandedBottomSelector,
  isExpandedTopSelector,
  setExpanded,
} from 'store/slices/slidesListSlice/slidesListSlice';
import { CollapseIcon } from 'images/icons/CollapseIcon';
import { ExpandIcon } from 'images/icons/ExpandIcon';
import { AnnotationAccordion } from 'components/OrderSlidesList/ExtAnnotations/AnnotationAccordion';

export const SlideViewerExtAnnotations = ({
  currentSlide,
  annotationsLegend,
  isTop,
}) => {
  const { classes } = useSlideViewerActionsStyles();
  const [
    isExportHaloAnnotationsModalOpen,
    setIsExportHaloAnnotationsModalOpen,
  ] = useState(false);
  const [expandedRows, setExpandedRows] = useState([]);
  const annotations = useSelector(annotationsSelector);
  const preferences = useSelector(currentUserPreferencesSelector);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const visibleAnnotations = preferences[DISPLAYED_EXTERNAL_ANNOTATIONS];
  const colorLegend = preferences[ANNOTATIONS_COLOR_LEGEND_FIELD];
  const isExpandedTop = useSelector(isExpandedTopSelector);
  const isExpandedBottom = useSelector(isExpandedBottomSelector);
  const isExpanded = isTop ? isExpandedTop : isExpandedBottom;
  const [currentAnnotationUUID, setCurrentAnnotationUUID] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const dispatch = useDispatch();
  const { showError, showSuccess } = useSnackbar();
  const externalAnnotations = currentSlide.external_annotations;
  const isExternalAnnotationsAvailable = !!externalAnnotations?.length;
  const [getSlide] = useLazyGetSlideQuery();
  const [deleteSlideAnnotation] = useDeleteExternalSlideAnnotationsMutation();
  const [uploadSlideAnnotation] = useUploadExternalSlideAnnotationsMutation();

  const onUpload = async (files) => {
    const promises = files.map((file) =>
      handleUploadAnnotationsAttachment(
        file,
        currentSlide,
        dispatch,
        preferences,
        showError,
        showSuccess,
        getSlide,
        setIsLoading,
        uploadSlideAnnotation,
      ),
    );
    await Promise.all(promises);
    setIsUploadModalOpen(false);
  };

  const handleChangeOpacity = (event, uuid, newValue) => {
    const updatedPreferences = {
      ...preferences,
      [ANNOTATIONS_OPACITY_FIELD]: {
        ...preferences[ANNOTATIONS_OPACITY_FIELD],
        [uuid]: newValue,
      },
    };
    dispatch(updatePreferences(updatedPreferences));
  };

  const toggleAnnotationVisibility = (uuid) => {
    const updatedPreferences = getUpdatedVisibility(preferences, uuid);
    dispatch(updatePreferences(updatedPreferences));
  };

  const onDelete = (annotationUUID) => {
    handleDeleteAnnotationsAttachment(
      currentSlide,
      annotationUUID,
      preferences,
      dispatch,
      setIsConfirmModalOpen,
      showError,
      showSuccess,
      getSlide,
      toggleAnnotationVisibility,
      setIsLoading,
      deleteSlideAnnotation,
    );
  };

  const onToggleExpanded = (uuid) => {
    setExpandedRows((prev) =>
      prev.includes(uuid) ? prev.filter((id) => id !== uuid) : [...prev, uuid],
    );
  };

  const onDownload = (url) => {
    saveAs(url);
  };

  const onDeleteClick = (e, uuid) => {
    e.stopPropagation();
    setIsConfirmModalOpen(true);
    setCurrentAnnotationUUID(uuid);
  };

  const onChangeVisibility = (e, uuid) => {
    e.stopPropagation();
    toggleAnnotationVisibility(uuid);
  };

  const isAnnotationsExist = !!annotations.find(
    (annotation) => annotation.slideUUID === currentSlide.uuid,
  )?.data.length;

  return (
    <>
      <Box className={classes.extAnnotationsActions}>
        <Box
          display="flex"
          flexDirection="row"
          margin="1rem 0 0 1.75rem"
          width="100%"
        >
          <Box
            display="flex"
            width="100%"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box
              display="flex"
              flexDirection="row"
              onClick={() => setIsUploadModalOpen(true)}
            >
              <Button className={classes.uploadButton}>
                <CloudUploadOutlined />
              </Button>
              <Box>
                <Typography className={classes.underline}>
                  Click to upload annotation file
                </Typography>
                <Typography color={LIGHT_GREY_2} marginLeft="0.25rem">
                  Supported Formats: .xml, .geojson, .annotations
                </Typography>
              </Box>
            </Box>
            <Button
              onClick={() => {
                dispatch(setExpanded({ isExpanded: !isExpanded, isTop }));
              }}
              variant="outlined"
              className={classes.expandButton}
            >
              {isExpanded ? <CollapseIcon /> : <ExpandIcon />}
            </Button>
          </Box>
        </Box>
      </Box>
      <Box className={classes.exportButtonWrapper}>
        <Button
          onClick={() => setIsExportHaloAnnotationsModalOpen(true)}
          variant="contained"
          color="primary"
          disabled={!isAnnotationsExist}
          className={classes.exportButton}
          startIcon={<FileDownloadOutlined />}
        >
          Export HistoWiz annotations
        </Button>
      </Box>
      {isLoading ? (
        <Box>
          {[1, 2, 3].map((i) => (
            <Skeleton
              key={i}
              variant="rectangular"
              className={classes.skeleton}
            />
          ))}
        </Box>
      ) : (
        isExternalAnnotationsAvailable &&
        externalAnnotations.map((annotation) => (
          <AnnotationAccordion
            handleChangeOpacity={handleChangeOpacity}
            annotation={annotation}
            visibleAnnotations={visibleAnnotations}
            expandedRows={expandedRows}
            onToggleExpanded={onToggleExpanded}
            onDownload={onDownload}
            onChangeVisibility={onChangeVisibility}
            annotationsLegend={annotationsLegend}
            dispatch={dispatch}
            preferences={preferences}
            classes={classes}
            colorLegend={colorLegend}
            key={annotation.uuid}
            onDeleteClick={onDeleteClick}
          />
        ))
      )}
      <UploadAnnotationsViewModal
        onUpload={onUpload}
        classes={classes}
        open={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
      />
      <DeleteModal
        label="Are you sure want to delete this annotations file?"
        open={!!isConfirmModalOpen}
        handleDeleteResult={() => onDelete(currentAnnotationUUID)}
        onClose={() => {
          setIsConfirmModalOpen(false);
          setCurrentAnnotationUUID(null);
        }}
      />
      <ExportHaloAnnotationsConfirmationModal
        slides={[currentSlide]}
        open={isExportHaloAnnotationsModalOpen}
        onClose={() => {
          setIsExportHaloAnnotationsModalOpen(false);
        }}
      />
    </>
  );
};
